import React from 'react';
import { Check, ArrowRight, Sparkles, Crown, TrendingUp, Phone, Shield } from 'lucide-react';
interface PricingSectionProps {
  title?: string;
  subtitle?: string;
  description?: string;
  'data-id'?: string;
}
export const PricingSection: React.FC<PricingSectionProps> = ({
  title = 'Beta Access',
  subtitle = 'Join Beta - Lock In 50% Off For Life',
  description = 'Limited spots available. Get early access and lifetime savings.',
  'data-id': dataId
}) => {
  return <section className="relative w-full min-h-screen bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212] py-24 md:py-32 px-6 md:px-8 flex items-center justify-center overflow-hidden" data-id="element-1572">
      {/* Subtle background decoration */}
      <div className="absolute inset-0 opacity-30" data-id="element-1573">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-10" data-id="element-1574" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-10" data-id="element-1575" />
      </div>

      <div className="relative max-w-[1280px] w-full flex flex-col items-center gap-16 z-10" data-id="element-1576">
        {/* Header */}
        <div className="flex flex-col items-center gap-8 max-w-4xl" data-id="element-1577">
          {/* Pill Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm" data-id="element-1578">
            <Crown className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-1579" />
            <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-1580">
              {title}
            </p>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-center leading-tight tracking-tight" data-id="element-1581">
            <span className="text-white" data-id="element-1582">Join Beta - </span>
            <span className="relative inline-block" data-id="element-1583">
              <span className="text-[#3ab795]" data-id="element-1584">Lock In 50% Off For Life</span>
              <span className="absolute bottom-2 left-0 w-full h-4 bg-[#3ab795]/20 -z-10" data-id="element-1585"></span>
            </span>
          </h1>

          {/* Description */}
          <p className="text-lg md:text-xl text-[#b8bcc8] text-center leading-relaxed max-w-3xl" data-id="element-1586">
            {description}
          </p>
        </div>

        {/* Single Beta Pricing Card */}
        <div className="w-full max-w-2xl" data-id="element-1587">
          {/* White glow effect - matching other cards */}
          <div className="relative" data-id="element-1588">
            <div className="absolute inset-0 rounded-[32px] blur-3xl bg-white opacity-30" style={{
            transform: 'scale(0.92)'
          }} data-id="element-1589" />

            <div className="relative bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-white/10 rounded-[32px] p-8 md:p-12 shadow-[0_8px_24px_rgba(0,0,0,0.3)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.4)] transition-all duration-300" data-id="element-1590">
              {/* Badge - "Limited Spots" */}
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10" data-id="element-1591">
                <span className="inline-flex items-center gap-1.5 bg-gradient-to-r from-[#3ab795] to-[#2d9575] text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg uppercase tracking-wide" data-id="element-1592">
                  <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-1593" />
                  <span data-id="element-1594">Limited Beta Spots</span>
                </span>
              </div>

              {/* Price */}
              <div className="text-center mb-8 pt-4" data-id="element-1595">
                <div className="flex items-center justify-center gap-2 mb-2" data-id="element-1596">
                  <span className="text-6xl md:text-7xl font-bold text-white" data-id="element-1597">
                    $199
                  </span>
                </div>
                <p className="text-lg text-[#b8bcc8]" data-id="element-1598">One-time investment</p>
              </div>

              {/* Divider */}
              <div className="h-px bg-white/10 mb-8" data-id="element-1599" />

              {/* Benefits */}
              <div className="space-y-4 mb-8" data-id="element-1600">
                <div className="flex items-start gap-3" data-id="element-1601">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#3ab795] flex items-center justify-center mt-0.5" data-id="element-1602">
                    <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-1603" />
                  </div>
                  <div data-id="element-1604">
                    <p className="text-lg font-semibold text-white" data-id="element-1605">
                      45-day trial period
                    </p>
                    <p className="text-sm text-[#b8bcc8]" data-id="element-1606">
                      Full access to test AcquireFlow risk-free
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-1607">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#3ab795] flex items-center justify-center mt-0.5" data-id="element-1608">
                    <TrendingUp className="w-4 h-4 text-white" strokeWidth={2.5} data-id="element-1609" />
                  </div>
                  <div data-id="element-1610">
                    <p className="text-lg font-semibold text-white" data-id="element-1611">
                      Lifetime 50% off all plans
                    </p>
                    <p className="text-sm text-[#b8bcc8]" data-id="element-1612">
                      Core: $499/mo (save $498/mo) • Scale: $799/mo (save
                      $798/mo)
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-1613">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#3ab795] flex items-center justify-center mt-0.5" data-id="element-1614">
                    <Sparkles className="w-4 h-4 text-white" strokeWidth={2.5} data-id="element-1615" />
                  </div>
                  <div data-id="element-1616">
                    <p className="text-lg font-semibold text-white" data-id="element-1617">
                      Priority early access
                    </p>
                    <p className="text-sm text-[#b8bcc8]" data-id="element-1618">
                      Get new features before public launch
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-1619">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#3ab795] flex items-center justify-center mt-0.5" data-id="element-1620">
                    <Phone className="w-4 h-4 text-white" strokeWidth={2.5} data-id="element-1621" />
                  </div>
                  <div data-id="element-1622">
                    <p className="text-lg font-semibold text-white" data-id="element-1623">
                      Free 1-on-1 setup call
                    </p>
                    <p className="text-sm text-[#b8bcc8]" data-id="element-1624">
                      $500 value - personalized onboarding with our team
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3" data-id="element-1625">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#3ab795] flex items-center justify-center mt-0.5" data-id="element-1626">
                    <Crown className="w-4 h-4 text-white" strokeWidth={2.5} data-id="element-1627" />
                  </div>
                  <div data-id="element-1628">
                    <p className="text-lg font-semibold text-white" data-id="element-1629">
                      Direct line to founders
                    </p>
                    <p className="text-sm text-[#b8bcc8]" data-id="element-1630">
                      Shape the product roadmap with your feedback
                    </p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-white/10 mb-8" data-id="element-1631" />

              {/* Value Highlight */}
              <div className="bg-[#3ab795]/5 border border-[#3ab795]/20 rounded-xl p-6 mb-8 text-center" data-id="element-1632">
                <p className="text-sm text-[#b8bcc8] mb-2" data-id="element-1633">
                  First-year value with Core plan:
                </p>
                <p className="text-3xl md:text-4xl font-bold text-[#3ab795]" data-id="element-1634">
                  $6,476+ in savings
                </p>
              </div>

              {/* CTA Button */}
              <a href="/trial" className="flex items-center justify-center gap-4 px-8 py-6 rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105 active:scale-95 group bg-[#3ab795] text-white shadow-[0_4px_16px_rgba(58,183,149,0.3)] hover:shadow-[0_8px_24px_rgba(58,183,149,0.4)] w-full" data-id="element-1635">
                <span data-id="element-1636">Join Beta Now</span>
                <ArrowRight className="w-6 h-6 transition-transform group-hover:translate-x-1" strokeWidth={2} data-id="element-1637" />
              </a>

              {/* Trust Badge */}
              <div className="flex items-center justify-center gap-2 mt-6" data-id="element-1638">
                <Shield className="w-4 h-4 text-[#3ab795]" strokeWidth={2.5} data-id="element-1639" />
                <span className="text-sm text-[#b8bcc8]" data-id="element-1640">
                  30-day money-back guarantee
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Line */}
        <div className="flex flex-col items-center gap-4 text-center max-w-2xl" data-id="element-1641">
          <div className="flex items-center gap-3" data-id="element-1642">
            <span className="text-2xl" data-id="element-1643">⚡</span>
            <p className="text-lg md:text-xl font-semibold text-white" data-id="element-1644">
              When we launch, you'll pay 50% less than everyone else — forever.
            </p>
          </div>
          <p className="text-sm text-[#b8bcc8]" data-id="element-1645">
            Join 500+ investors already using AcquireFlow to automate their deal
            flow
          </p>
        </div>
      </div>
    </section>;
};